import React, { Component, createRef } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { Capacitor } from '@capacitor/core';
import { isNativePlatform } from '@/utils/platform';
import { getGoogleClientId } from '@/config/environment';
import styles from './GoogleSignIn.module.scss';

let SocialLogin: any = null;

interface GoogleSignInProps {
  onSuccess: (credentialResponse: any) => void;
  onError?: (error?: any) => void;
  useOneTap?: boolean;
}

interface GoogleSignInState {
  isLoading: boolean;
  isInitialized: boolean;
  useWebFallback: boolean;
}

const GoogleSvg = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" className={styles.googleIcon}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

class GoogleSignIn extends Component<GoogleSignInProps, GoogleSignInState> {
  private googleBtnRef = createRef<HTMLDivElement>();

  constructor(props: GoogleSignInProps) {
    super(props);
    this.state = {
      isLoading: false,
      isInitialized: false,
      useWebFallback: false
    };
  }

  async componentDidMount() {
    const clientId = getGoogleClientId();
    if (!clientId || clientId.includes('your-') || clientId.length < 20) {
      console.error('[GoogleSignIn] Invalid or missing Google Client ID:', clientId);
    } else {
      console.log('[GoogleSignIn] Google Client ID configured:', clientId.substring(0, 20) + '...');
    }

    if (Capacitor.isNativePlatform()) {
      try {
        if (!SocialLogin) {
          const module = await import('@capgo/capacitor-social-login');
          SocialLogin = module.SocialLogin;
        }
        if (SocialLogin) {
          this.setState({ isInitialized: true });
          console.log('[GoogleSignIn] Native Social Login ready');
        }
      } catch (error) {
        console.log('[GoogleSignIn] Native Social Login not available (web build)');
      }
    }
  }

  handleMobileGoogleSignIn = async () => {
    if (!this.state.isInitialized && Capacitor.isNativePlatform()) {
      try {
        if (!SocialLogin) {
          const module = await import('@capgo/capacitor-social-login');
          SocialLogin = module.SocialLogin;
        }
        if (SocialLogin) {
          this.setState({ isInitialized: true });
        } else {
          throw new Error('SocialLogin plugin not available');
        }
      } catch (error: any) {
        const errorMessage = error?.message || '';
        if (errorMessage.includes('not implemented') || errorMessage.includes('not available')) {
          this.setState({ useWebFallback: true });
          return;
        }
        if (this.props.onError) {
          this.props.onError({ type: 'initialization_error', message: errorMessage || 'Failed to initialize Google Sign-In', originalError: error });
        }
        return;
      }
    }

    if (!SocialLogin) {
      this.setState({ useWebFallback: true });
      return;
    }

    this.setState({ isLoading: true });

    try {
      const result = await SocialLogin.login({
        provider: 'google',
        options: { scopes: ['profile', 'email'] },
      });

      const resultAny = result as any;
      const idToken = resultAny?.result?.authentication?.idToken ||
                      resultAny?.result?.idToken ||
                      resultAny?.authentication?.idToken;

      if (!idToken) {
        throw new Error('No ID token received from Google Sign-In');
      }

      this.props.onSuccess({ credential: idToken });
    } catch (error: any) {
      this.setState({ isLoading: false });
      const errorMessage = error?.message || '';
      if (errorMessage.includes('not implemented') || errorMessage.includes('not available')) {
        this.setState({ useWebFallback: true });
        return;
      }

      let errorType = 'unknown_error';
      let userErrorMessage = 'Google sign-in failed. Please try again.';
      if (errorMessage.includes('cancel') || errorMessage.includes('dismiss')) {
        errorType = 'user_cancelled';
        userErrorMessage = 'Sign-in was cancelled. Please try again.';
      } else if (errorMessage.includes('network') || errorMessage.includes('connection')) {
        errorType = 'network_error';
        userErrorMessage = 'Network error. Please check your connection and try again.';
      }

      if (this.props.onError) {
        this.props.onError({ type: errorType, message: userErrorMessage, originalError: error });
      }
    }
  };

  render() {
    const { onSuccess, onError, useOneTap } = this.props;
    const { isLoading, useWebFallback } = this.state;
    const isMobile = isNativePlatform();
    const clientId = getGoogleClientId();
    const isValidClientId = clientId && !clientId.includes('your-') && clientId.length >= 20;

    // Web or web-fallback: overlay invisible GoogleLogin on top of styled button
    if (!isMobile || useWebFallback) {
      const handleWebError = (error?: any) => {
        console.error('[GoogleSignIn] Web Google Sign-In Error:', error);
        const errStr = error && (typeof error === 'string' ? error : (error?.message || error?.error || JSON.stringify(error)));
        const isOriginError = typeof errStr === 'string' && /not a valid origin|origin is not allowed|has not been whitelisted|invalid_client/i.test(errStr);
        let errorType = 'unknown_error';
        let errorMessage = 'Google sign-in failed. Please try again.';
        if (!isValidClientId) {
          errorType = 'configuration_error';
          errorMessage = 'Google Sign-In is not properly configured. Please check your Google Client ID.';
        } else if (isOriginError) {
          errorType = 'configuration_error';
          const origin = typeof window !== 'undefined' ? window.location.origin : '';
          errorMessage = `This site (${origin}) is not allowed for your Google OAuth client. Add it in Google Cloud Console.`;
        } else {
          errorType = 'signin_failed';
          errorMessage = 'Google sign-in was cancelled or failed. Please try again.';
        }
        if (onError) {
          onError({ type: errorType, message: errorMessage, originalError: error });
        }
      };

      if (!isValidClientId) {
        return (
          <div className={styles.googleSignInContainer}>
            <button
              type="button"
              className={`${styles.googleBtn} ${styles.googleBtnDisabled}`}
              onClick={() => onError?.({ type: 'configuration_error', message: 'Google Sign-In is not configured. Please set VITE_GOOGLE_CLIENT_ID in .env file.' })}
            >
              <GoogleSvg />
              <span>Sign in with Google</span>
              <span className={styles.googleBtnBadge}>Not configured</span>
            </button>
          </div>
        );
      }

      return (
        <div className={styles.googleSignInContainer}>
          <div className={styles.googleBtnWrapper}>
            {/* Our visible styled button */}
            <div className={styles.googleBtn} aria-hidden="true">
              <GoogleSvg />
              <span>Continue with Google</span>
            </div>
            {/* Invisible real Google button on top to capture clicks */}
            <div className={styles.googleBtnOverlay} ref={this.googleBtnRef}>
              <GoogleLogin
                onSuccess={onSuccess}
                onError={handleWebError}
                useOneTap={useOneTap && !isMobile}
                size="large"
                width={360}
                shape="pill"
                theme="filled_black"
              />
            </div>
          </div>
        </div>
      );
    }

    if (useWebFallback) return null;

    // Native mobile: custom styled button
    return (
      <div className={styles.googleSignInContainer}>
        <button
          type="button"
          onClick={this.handleMobileGoogleSignIn}
          disabled={isLoading}
          className={styles.googleBtn}
        >
          {isLoading ? (
            <span className={styles.googleBtnSpinner} />
          ) : (
            <GoogleSvg />
          )}
          <span>{isLoading ? 'Connecting…' : 'Continue with Google'}</span>
        </button>
      </div>
    );
  }
}

export default GoogleSignIn;
